import { rtkApi } from '@/shared/api/rtkApi';
import type { ClubRole } from '@/entities/Club';
import type { AccountDeletionSummary, AccountLifecycleStatus } from '@/shared/model/types/accountDeletion';

export interface ClubItemApi {
  id: number;
  name: string | null;
  region: string | null;
  lastName: string | null;
  firstName: string | null;
  patronymic: string | null;
  contactPhone: string | null;
  loginPrefix: string | null;
  legalName: string | null;
  isActive: boolean;
  accountStatus?: AccountLifecycleStatus;
  deletion?: AccountDeletionSummary | null;
  balanceTechnocoins?: number | null;
  balanceLimit?: number | null;
  unlimitedUntil?: string | null;
  payerInn?: string | null;
  birthDate?: string | null;
  adminsCount?: number;
  playersCount?: number;
  isChanged?: boolean;
  clubType: 'individual' | 'legal';
}

export interface ClubAccountApi {
  id: number;
  login: string | null;
  role: ClubRole | null;
  hardwareId: string | null;
  isActive: boolean | null;
}

export interface ClubDetailsApi {
  id: number;
  name: string | null;
  region: string | null;
  loginPrefix: string | null;
  isActive: boolean;
  accountStatus?: AccountLifecycleStatus;
  deletion?: AccountDeletionSummary | null;
  balanceTechnocoins?: number | null;
  balanceLimit?: number | null;
  unlimitedUntil?: string | null;
  lastLimitCheckAt?: string | null;
  limitBlockedAt?: string | null;
  lastName: string | null;
  firstName: string | null;
  patronymic: string | null;
  legalName: string | null;
  contactPhone: string | null;
  payerInn: string | null;
  birthDate: string | null;
  clubType: 'individual' | 'legal';
  accounts?: ClubAccountApi[] | null;
}

export interface BalanceOperationItem {
  id: number;
  type: 'deposit' | 'withdrawal';
  reason: string;
  technocoins: number;
  editorId: number | null;
  editor?: { id: number; fullName: string | null } | null;
  createdAt: string;
}

export interface ClubBalanceResponse {
  clubId: number;
  balanceTechnocoins: number;
  balanceLimit: number;
  unlimitedUntil: string | null;
  items: BalanceOperationItem[];
}

export interface DepositBalanceRequest {
  clubId: number;
  technocoins: number;
  expiresAt?: string | null;
  reason: string;
  amountRubles?: number | null;
}

export interface WithdrawBalanceRequest {
  clubId: number;
  technocoins: number;
  reason: string;
}

export interface ClubTimeSpentItem {
  id: number;
  type: 'server' | 'training';
  privateServerId: string | null;
  technocoinsSpent: number | null;
  durationSeconds: number | null;
  maxPlayers: number | null;
  createdAt: string | null;
  creatorId: number | null;
  creatorLogin: string | null;
  name: string | null;
  ip: string | null;
  port: number | null;
  closedAt: string | null;
  isActive: boolean | null;
  userLogin: string | null;
  roles?: Record<string, { count: number; technocoinsSpent: number }> | null;
}

export interface ClubTimeSpentResponse {
  items: ClubTimeSpentItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface ClubServerHistoryItem {
  privateServerId: string | null;
  name: string | null;
  ip: string | null;
  port: number | null;
  createdAt: string | null;
  closedAt: string | null;
  isActive: boolean | null;
  totalTimeSeconds: number | null;
  maxPlayers: number | null;
  creatorLogin: string | null;
}

export interface ClubServersHistoryResponse {
  items: ClubServerHistoryItem[];
  totalSeconds: number;
}

export interface PatchClubRequest {
  id: number;
  data: Partial<{
    name: string;
    region: string | null;
    lastName: string | null;
    firstName: string | null;
    patronymic: string | null;
    legalName: string | null;
    contactPhone: string | null;
    birthDate: string | null;
    payerInn: string | null;
    loginPrefix: string;
    isActive: boolean;
  }>;
}

export interface ClubChangeLogItem {
  id: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  type?: string | null;
  reason?: string | null;
  editor: { id: number; fullName: string | null } | null;
  createdAt: string | null;
}

const clubApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getClubs: build.query<{ items: ClubItemApi[] }, void>({
      query: () => `/v1/superadmin/clubs`,
      providesTags: ['ClubTable'],
    }),
    getClubDetails: build.query<ClubDetailsApi, number>({
      query: (id) => `/v1/superadmin/clubs/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'ClubTable', id } as any],
    }),
    patchClub: build.mutation<{ status: string } | ClubItemApi, PatchClubRequest>({
      query: ({ id, data }) => ({
        url: `/v1/superadmin/clubs/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['ClubTable', { type: 'ClubLog', id } as any],
    }),
    addClubAccount: build.mutation<
      { status: string; created: number },
      { id: number; role: 'admin' | 'player' | 'observer' | 'developer' | 'viewer'; count?: number }
    >({
      query: ({ id, role, count = 1 }) => ({
        url: `/v1/superadmin/clubs/${id}/accounts`,
        method: 'POST',
        body: { role, count },
      }),
      invalidatesTags: ['ClubTable'],
    }),
    removeClubAccount: build.mutation<
      { status: string; deactivated: number },
      { id: number; role: 'admin' | 'player' | 'observer' | 'developer' | 'viewer'; count?: number }
    >({
      query: ({ id, role, count = 1 }) => ({
        url: `/v1/superadmin/clubs/${id}/accounts?role=${role}&count=${count}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ClubTable'],
    }),
    changeClubAccountsPassword: build.mutation<{ status: string }, { id: number; password: string }>({
      query: ({ id, password }) => ({
        url: `/v1/superadmin/clubs/${id}/accounts/password`,
        method: 'PATCH',
        body: { password },
      }),
      invalidatesTags: ['ClubTable'],
    }),
    unlinkClubHardware: build.mutation<{ status: string }, number[]>({
      query: (ids) => ({
        url: `/v1/superadmin/clubs/unlink-hardware/${ids.join(';')}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ClubTable'],
    }),
    deleteClub: build.mutation<{ status: string }, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/v1/superadmin/clubs/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['ClubTable'],
    }),
    restoreClub: build.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/v1/superadmin/clubs/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => ['ClubTable', { type: 'ClubLog', id }],
    }),
    getClubChangeLog: build.query<ClubChangeLogItem[], { clubId: number; offset: number; limit?: number }>({
      query: ({ clubId, offset, limit = 20 }) =>
        `/v1/superadmin/clubs/${clubId}/change-log?offset=${offset}&limit=${limit}`,
      providesTags: (_result, _error, { clubId }) => [{ type: 'ClubLog', id: clubId } as any],
      serializeQueryArgs: ({ queryArgs }) => ({ clubId: queryArgs.clubId }),
      merge: (currentCache, newItems) => {
        if (newItems.length > 0) {
          const newIds = new Set(newItems.map((item) => item.id));
          const uniqueCurrent = currentCache.filter((item) => !newIds.has(item.id));
          uniqueCurrent.push(...newItems);
          return uniqueCurrent.sort((a, b) => b.id - a.id);
        }
        return currentCache;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset !== previousArg?.offset;
      },
    }),
    getClubServersHistory: build.query<
      ClubServersHistoryResponse,
      { clubId: number; startAt?: string; endAt?: string; timezone?: string; limit?: number; offset?: number }
    >({
      query: ({ clubId, startAt, endAt, timezone, limit, offset }) => {
        const params: string[] = [];
        if (startAt) params.push(`startAt=${encodeURIComponent(startAt)}`);
        if (endAt) params.push(`endAt=${encodeURIComponent(endAt)}`);
        if (timezone) params.push(`timezone=${encodeURIComponent(timezone)}`);
        if (typeof limit === 'number') params.push(`limit=${limit}`);
        if (typeof offset === 'number') params.push(`offset=${offset}`);
        const qs = params.length ? `?${params.join('&')}` : '';
        return `/v1/superadmin/clubs/${clubId}/servers-history${qs}`;
      },
    }),

    getBalance: build.query<ClubBalanceResponse, number>({
      query: (clubId) => `/v1/superadmin/clubs/${clubId}/balance`,
      providesTags: (_res, _err, clubId) => [{ type: 'ClubBalance', id: clubId } as any],
    }),

    depositBalance: build.mutation<{ clubId: number; technocoins: number }, DepositBalanceRequest>({
      query: ({ clubId, ...body }) => ({
        url: `/v1/superadmin/clubs/${clubId}/balance/deposit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, { clubId }) => [
        { type: 'ClubBalance', id: clubId } as any,
        { type: 'ClubTable', id: clubId } as any,
        'ClubTable',
      ],
    }),

    withdrawBalance: build.mutation<
      { status: string; clubId: number; balanceTechnocoins: number },
      WithdrawBalanceRequest
    >({
      query: ({ clubId, ...body }) => ({
        url: `/v1/superadmin/clubs/${clubId}/balance/withdraw`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, { clubId }) => [
        { type: 'ClubBalance', id: clubId } as any,
        { type: 'ClubTable', id: clubId } as any,
        { type: 'ClubLog', id: clubId } as any,
        'ClubTable',
      ],
    }),

    getClubTimeSpent: build.query<ClubTimeSpentResponse, { clubId: number; limit?: number; offset?: number }>({
      query: ({ clubId, limit = 50, offset = 0 }) =>
        `/v1/superadmin/clubs/${clubId}/time-spent?limit=${limit}&offset=${offset}`,
      serializeQueryArgs: ({ queryArgs }) => ({ clubId: queryArgs.clubId }),
      merge: (currentCache, newResp) => {
        if (!currentCache || newResp.offset === 0) {
          return newResp;
        }
        const existing = currentCache.items ?? [];
        const nextItems = newResp.items ?? [];
        const existingIds = new Set(existing.map((i) => i.id));
        const mergedItems = [...existing, ...nextItems.filter((i) => !existingIds.has(i.id))];
        return { ...newResp, items: mergedItems };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset !== previousArg?.offset;
      },
    }),
  }),
});

export const {
  useGetClubsQuery,
  usePatchClubMutation,
  useDeleteClubMutation,
  useRestoreClubMutation,
  useGetClubDetailsQuery,
  useAddClubAccountMutation,
  useRemoveClubAccountMutation,
  useChangeClubAccountsPasswordMutation,
  useUnlinkClubHardwareMutation,
  useGetClubChangeLogQuery,
  useGetClubServersHistoryQuery,
  useGetBalanceQuery,
  useDepositBalanceMutation,
  useWithdrawBalanceMutation,
  useGetClubTimeSpentQuery,
} = clubApi;
